
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { checkAdminAuthorization } from "@/utils/authHelpers";

interface NavigationContentProps {
  onClearPrompt?: () => void;
}

export const NavigationContent = ({
  onClearPrompt = () => {}
}: NavigationContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      setIsCheckingAdmin(true);
      try {
        const isAdminUser = await checkAdminAuthorization(navigate, (isAdmin) => {
          setIsAdmin(isAdmin);
        });
        setIsAdmin(isAdminUser);
      } catch (error) {
        console.error('❌ [Nav] Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  const linkClass = (active: boolean) =>
    `text-sm font-medium transition-colors hover:text-gray-900 ${
      active ? 'text-blue-600' : 'text-gray-500'
    }`;

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center">
        <nav className="flex items-center gap-6">
          <Link
            to="/dashboard"
            className={linkClass(location.pathname === '/dashboard')}
          >
            Dashboard
          </Link>
          <Link
            to="/companies"
            className={linkClass(location.pathname === '/companies')}
          >
            Companies
          </Link>
          <Link
            to="/diffs2"
            className={linkClass(
              location.pathname === '/diffs2' || location.pathname.startsWith('/diffs2/')
            )}
          >
            Diffs
          </Link>
          <Link
            to="/tasks"
            className={linkClass(
              location.pathname === '/tasks' || location.pathname.startsWith('/tasks/')
            )}
          >
            Tasks
          </Link>
          <Link
            to="/digests"
            className={linkClass(location.pathname.startsWith('/digests'))}
          >
            Digests
          </Link>
          <Link
            to="/requests"
            className={linkClass(location.pathname.startsWith('/requests'))}
          >
            Requests
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              className="text-gray-500 hover:text-gray-900 transition-colors"
              aria-label="More navigation"
            >
              <MoreVertical className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/collections">Collections</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/search">Search</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </div>
  );
};
